import { maxDatapointAgeForDaysLeft } from "motw_core/Entity/IGoalDataPoint";
import * as React from "react";
import { Style } from "source/app/presentation/Shared/Styles/CSS";
import Player from "../../../../../domain/Player";
import { IGoalWithDatapoints } from "motw_core/Repositories/DatapointRepository";
import IconWithText from "../../../../../services/Shared/IconWithText/IconWithText";
import { Sounds } from "../../../../../services/Sound/Sounds";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Render from "../../../../Shared/Render";
import {
    maxGoodDaysLeft,
    minGoodDaysLeft,
} from "../../GoalCards/Helpers/GoalCardsConstants";
import { CalculateDaysLeft } from "../../GoalHelpers/CalculateDaysLeft";
import GoalEditorPanel from "./GoalEditorPanel";
import { HandleValueChange } from "./HandleValueChange";

export function ShowNewDaysLeft({ GEP }: { GEP: GoalEditorPanel }) {
    const prevData = GEP.state.previousState;
    const currData = GEP.state.currentState;

    const prevWeeklyRate = Number.parseFloat(prevData.weeklyrate.toString());
    const currWeeklyRate = Number.parseFloat(currData.weeklyrate.toString());
    const prevStartDate = prevData.startdate;
    const currStartDate = currData.startdate;
    let newDaysLeft = prevData.daysleft;

    // Calculating only when specific values change
    // to prevent an infinite loop of re-renderings
    if (currWeeklyRate !== prevWeeklyRate || prevStartDate !== currStartDate) {
        const calculationInput: IGoalWithDatapoints = {
            ...currData,
            daysleft: Player.initialGoalDaysLeft,
        };

        newDaysLeft = CalculateDaysLeft(calculationInput);
    }

    const isDaysLeftCentered =
        Math.round(newDaysLeft) === maxDatapointAgeForDaysLeft;
    const isDaysLeftMaximized = Math.round(newDaysLeft) === maxGoodDaysLeft;

    return (
        <div>
            <NewDaysLeftText daysleft={newDaysLeft} />
            <Render id="CenterDaysLeft" when={!isDaysLeftCentered}>
                <CenterDaysLeft GEP={GEP} />
            </Render>
            <Render id="MaximizeDaysLeft" when={!isDaysLeftMaximized}>
                <MaximizeDaysLeft GEP={GEP} />
            </Render>
        </div>
    );
}

function NewDaysLeftText({ daysleft }: { daysleft: number }) {
    const textColor =
        daysleft >= maxGoodDaysLeft + 1 || daysleft < minGoodDaysLeft
            ? `coral`
            : `white`;

    const adjustedDaysLeft = Math.floor(daysleft - minGoodDaysLeft);

    return (
        <span
            {...Style(`newDaysLeftText_${daysleft}`, {
                color: textColor,
            })}
        >{`${adjustedDaysLeft} days left`}</span>
    );
}

function CenterDaysLeft({ GEP }: { GEP: GoalEditorPanel }) {
    const currData = GEP.state.currentState;
    const currDaysLeft = currData.daysleft;
    const currWeeklyRate = Number.parseFloat(currData.weeklyrate.toString());

    const newWeeklyRate =
        (currDaysLeft / maxDatapointAgeForDaysLeft) * currWeeklyRate;

    const roundedNewWeeklyRate = LimitWeeklyRateDecimalPlaces(newWeeklyRate);

    return (
        <IconWithText
            iconSuffix={EGlyphicon["align-center"]}
            whenClicked={() => {
                Sounds.grunt.Play();
                HandleValueChange(GEP, roundedNewWeeklyRate, "weeklyrate");
            }}
            displayAsBlock={false}
            tooltip="re-align expectations"
        />
    );
}

function MaximizeDaysLeft({ GEP }: { GEP: GoalEditorPanel }) {
    const currData = GEP.state.currentState;
    const currDaysLeft = currData.daysleft;
    const currWeeklyRate = Number.parseFloat(currData.weeklyrate.toString());
    const newWeeklyRate = (currDaysLeft / maxGoodDaysLeft) * currWeeklyRate;
    const roundedNewWeeklyRate = LimitWeeklyRateDecimalPlaces(newWeeklyRate);

    return (
        <IconWithText
            iconSuffix={EGlyphicon["align-right"]}
            whenClicked={() => {
                Sounds.shoutWhoo.Play();
                HandleValueChange(GEP, roundedNewWeeklyRate, "weeklyrate");
            }}
            displayAsBlock={false}
            tooltip="level up!"
        />
    );
}

export function LimitWeeklyRateDecimalPlaces(weeklyRate: number) {
    return weeklyRate >= 100
        ? Number.parseInt(weeklyRate.toString())
        : Number.parseFloat(weeklyRate.toFixed(2));
}
